import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import Card from '../components/Card'
import Button from '../components/Button'
import Input from '../components/Input'
import Textarea from '../components/Textarea'
import Select from '../components/Select'
import {
  FileText,
  Search,
  CheckCircle2,
  XCircle,
  Clock,
  Mail,
  Phone,
  Eye,
  MessageSquare,
  Building2,
  User,
  Calendar,
  DollarSign,
  Filter,
} from 'lucide-react'
import {
  getAllOrganizationRequests,
  updateOrganizationRequestStatus,
  approveOrganizationRequest,
  addContactNotes,
  getOrganizationRequest,
} from '../services/organizationRequests'
import { formatDate } from '../utils/helpers'
import { useToast } from '../contexts/ToastContext'

const MasterAdminOrganizationRequests = () => {
  const { success, error: showError } = useToast()
  const [requests, setRequests] = useState([])
  const [filteredRequests, setFilteredRequests] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [selectedRequest, setSelectedRequest] = useState(null)
  const [showDetailsModal, setShowDetailsModal] = useState(false)
  const [showApprovalModal, setShowApprovalModal] = useState(false)
  const [showContactModal, setShowContactModal] = useState(false)
  const [approvalData, setApprovalData] = useState({
    name: '',
    tier: 'demo',
  })
  const [contactData, setContactData] = useState({
    notes: '',
    method: 'email',
  })

  useEffect(() => {
    loadRequests()
  }, [])

  useEffect(() => {
    filterRequests()
  }, [requests, searchTerm, statusFilter])

  const loadRequests = async () => {
    try {
      setLoading(true)
      const result = await getAllOrganizationRequests()
      if (result.success) {
        setRequests(result.data)
      } else {
        showError(result.error || 'Failed to load requests', { title: 'Error' })
      }
    } catch (error) {
      console.error('Error loading requests:', error)
      showError('Failed to load requests', { title: 'Error' })
    } finally {
      setLoading(false)
    }
  }

  const filterRequests = () => {
    let filtered = requests

    if (statusFilter !== 'all') {
      filtered = filtered.filter((r) => r.status === statusFilter)
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase()
      filtered = filtered.filter(
        (r) =>
          r.requested_organization_name?.toLowerCase().includes(term) ||
          r.contact_email?.toLowerCase().includes(term) ||
          r.contact_name?.toLowerCase().includes(term) ||
          r.user_email?.toLowerCase().includes(term)
      )
    }

    setFilteredRequests(filtered)
  }

  const handleViewDetails = async (requestId) => {
    try {
      const result = await getOrganizationRequest(requestId)
      if (result.success) {
        setSelectedRequest(result.data)
        setShowDetailsModal(true)
      }
    } catch (error) {
      console.error('Error loading request details:', error)
    }
  }

  const handleApprove = async () => {
    if (!selectedRequest) return

    try {
      const result = await approveOrganizationRequest(selectedRequest.id, {
        name: approvalData.name || selectedRequest.requested_organization_name,
        tier: approvalData.tier,
      })

      if (result.success) {
        success('Organization request approved and organization created successfully!', {
          title: 'Success',
        })
        setShowApprovalModal(false)
        setShowDetailsModal(false)
        loadRequests()
      } else {
        showError(result.error || 'Failed to approve request', { title: 'Error' })
      }
    } catch (error) {
      console.error('Error approving request:', error)
      showError('Failed to approve request', { title: 'Error' })
    }
  }

  const handleReject = async (requestId, notes) => {
    try {
      const result = await updateOrganizationRequestStatus(requestId, {
        status: 'rejected',
        review_notes: notes,
      })

      if (result.success) {
        success('Request rejected successfully', { title: 'Success' })
        loadRequests()
      } else {
        showError(result.error || 'Failed to reject request', { title: 'Error' })
      }
    } catch (error) {
      console.error('Error rejecting request:', error)
      showError('Failed to reject request', { title: 'Error' })
    }
  }

  const handleMarkContacted = async () => {
    if (!selectedRequest) return

    try {
      const result = await addContactNotes(
        selectedRequest.id,
        contactData.notes,
        contactData.method
      )

      if (result.success) {
        success('Contact notes added successfully', { title: 'Success' })
        setShowContactModal(false)
        setShowDetailsModal(false)
        loadRequests()
      } else {
        showError(result.error || 'Failed to add contact notes', { title: 'Error' })
      }
    } catch (error) {
      console.error('Error adding contact notes:', error)
      showError('Failed to add contact notes', { title: 'Error' })
    }
  }

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-700',
      under_review: 'bg-blue-100 text-blue-700',
      contacted: 'bg-purple-100 text-purple-700',
      approved: 'bg-green-100 text-green-700',
      rejected: 'bg-red-100 text-red-700',
    }

    return (
      <span
        className={`px-3 py-1 rounded-full text-xs font-semibold ${
          styles[status] || 'bg-gray-100 text-gray-700'
        }`}
      >
        {status.replace('_', ' ')}
      </span>
    )
  }

  if (loading) {
    return (
      <div className="section-container">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="section-container">
      <div className="mb-6">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2 flex items-center gap-3">
          <FileText size={32} />
          Organization Requests
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          Review and manage requests for new wellness centers and organizations
        </p>
      </div>

      {/* Filters */}
      <Card variant="soft" className="p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <Input
                placeholder="Search by name, email, or organization..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
          <div className="w-full md:w-48">
            <Select
              label="Status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              options={[
                { label: 'All Statuses', value: 'all' },
                { label: 'Pending', value: 'pending' },
                { label: 'Under Review', value: 'under_review' },
                { label: 'Contacted', value: 'contacted' },
                { label: 'Approved', value: 'approved' },
                { label: 'Rejected', value: 'rejected' },
              ]}
            />
          </div>
        </div>
      </Card>

      {/* Requests List */}
      <div className="space-y-4">
        {filteredRequests.length === 0 ? (
          <Card variant="soft" className="p-12 text-center">
            <FileText size={48} className="mx-auto mb-4 text-gray-400" />
            <p className="text-gray-500 dark:text-gray-400">No requests found</p>
          </Card>
        ) : (
          filteredRequests.map((request) => (
            <Card key={request.id} variant="soft" className="p-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {request.requested_organization_name}
                    </h3>
                    {getStatusBadge(request.status)}
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-gray-600 dark:text-gray-400">
                    <div className="flex items-center gap-2">
                      <User size={16} />
                      <span>{request.contact_name || request.user_name}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Mail size={16} />
                      <span>{request.contact_email}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Building2 size={16} />
                      <span className="capitalize">{request.organization_type?.replace('_', ' ')}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Calendar size={16} />
                      <span>{formatDate(request.created_at)}</span>
                    </div>
                  </div>
                  {request.main_challenges && (
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-2 line-clamp-2">
                      {request.main_challenges}
                    </p>
                  )}
                </div>
                <div className="flex gap-2 ml-4">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => handleViewDetails(request.id)}
                  >
                    <Eye size={16} className="mr-2" />
                    View
                  </Button>
                </div>
              </div>
            </Card>
          ))
        )}
      </div>

      {/* Details Modal */}
      <AnimatePresence>
        {showDetailsModal && selectedRequest && (
          <>
            <motion.div
              className="fixed inset-0 bg-black/30 backdrop-blur-sm z-40"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowDetailsModal(false)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="fixed inset-0 z-50 flex items-center justify-center p-4"
            >
              <Card variant="glass" className="w-full max-w-4xl max-h-[90vh] overflow-y-auto p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                    Request Details
                  </h2>
                  <button
                    onClick={() => setShowDetailsModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XCircle size={24} />
                  </button>
                </div>

                <div className="space-y-6">
                  {/* Organization Details */}
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                      <Building2 size={20} />
                      Organization Details
                    </h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-gray-600 dark:text-gray-400">Name</p>
                        <p className="font-medium text-gray-900 dark:text-white">{selectedRequest.requested_organization_name}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600 dark:text-gray-400">Type</p>
                        <p className="font-medium text-gray-900 dark:text-white capitalize">
                          {selectedRequest.organization_type?.replace('_', ' ')}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600 dark:text-gray-400">Expected Users</p>
                        <p className="font-medium text-gray-900 dark:text-white">{selectedRequest.expected_number_of_users || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600 dark:text-gray-400">Expected Clients</p>
                        <p className="font-medium text-gray-900 dark:text-white">{selectedRequest.expected_number_of_clients || 'N/A'}</p>
                      </div>
                    </div>
                    {selectedRequest.organization_description && (
                      <div className="mt-4">
                        <p className="text-sm text-gray-600 dark:text-gray-400">Description</p>
                        <p className="text-gray-900 dark:text-white">{selectedRequest.organization_description}</p>
                      </div>
                    )}
                  </div>

                  {/* Contact Information */}
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                      <Phone size={20} />
                      Contact Information
                    </h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-gray-600 dark:text-gray-400">Name</p>
                        <p className="font-medium text-gray-900 dark:text-white">{selectedRequest.contact_name}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600 dark:text-gray-400">Email</p>
                        <p className="font-medium text-gray-900 dark:text-white">{selectedRequest.contact_email}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600 dark:text-gray-400">Phone</p>
                        <p className="font-medium text-gray-900 dark:text-white">{selectedRequest.contact_phone || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600 dark:text-gray-400">Preferred Method</p>
                        <p className="font-medium text-gray-900 dark:text-white capitalize">{selectedRequest.preferred_contact_method}</p>
                      </div>
                    </div>
                  </div>

                  {/* Marketing & UX Data */}
                  {selectedRequest.current_pain_points?.length > 0 && (
                    <div>
                      <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Pain Points</h3>
                      <div className="flex flex-wrap gap-2">
                        {selectedRequest.current_pain_points.map((point, idx) => (
                          <span
                            key={idx}
                            className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm"
                          >
                            {point}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {selectedRequest.expectations?.length > 0 && (
                    <div>
                      <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Expectations</h3>
                      <div className="flex flex-wrap gap-2">
                        {selectedRequest.expectations.map((exp, idx) => (
                          <span
                            key={idx}
                            className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm"
                          >
                            {exp}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {selectedRequest.desired_features?.length > 0 && (
                    <div>
                      <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Desired Features</h3>
                      <div className="flex flex-wrap gap-2">
                        {selectedRequest.desired_features.map((feature, idx) => (
                          <span
                            key={idx}
                            className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm"
                          >
                            {feature}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {selectedRequest.main_challenges && (
                    <div>
                      <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Main Challenges</h3>
                      <p className="text-gray-700 dark:text-gray-300">{selectedRequest.main_challenges}</p>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Budget Range</p>
                      <p className="font-medium text-gray-900 dark:text-white capitalize">
                        {selectedRequest.budget_range?.replace('_', ' ')}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Timeline</p>
                      <p className="font-medium text-gray-900 dark:text-white capitalize">
                        {selectedRequest.timeline?.replace('_', ' ')}
                      </p>
                    </div>
                  </div>

                  {selectedRequest.how_did_you_hear_about_us && (
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">How did you hear about us?</p>
                      <p className="font-medium text-gray-900 dark:text-white">{selectedRequest.how_did_you_hear_about_us}</p>
                    </div>
                  )}

                  {selectedRequest.what_made_you_interested && (
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">What made you interested?</p>
                      <p className="font-medium text-gray-900 dark:text-white">{selectedRequest.what_made_you_interested}</p>
                    </div>
                  )}

                  {selectedRequest.additional_notes && (
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Additional Notes</p>
                      <p className="font-medium text-gray-900 dark:text-white">{selectedRequest.additional_notes}</p>
                    </div>
                  )}

                  {/* Actions */}
                  {selectedRequest.status === 'pending' || selectedRequest.status === 'under_review' ? (
                    <div className="flex gap-3 pt-4 border-t">
                      <Button
                        onClick={() => {
                          setApprovalData({
                            name: selectedRequest.requested_organization_name,
                            tier: 'demo',
                          })
                          setShowApprovalModal(true)
                        }}
                        className="flex-1"
                      >
                        <CheckCircle2 size={18} className="mr-2" />
                        Approve
                      </Button>
                      <Button
                        variant="secondary"
                        onClick={() => {
                          const notes = prompt('Rejection reason (optional):')
                          if (notes !== null) {
                            handleReject(selectedRequest.id, notes)
                            setShowDetailsModal(false)
                          }
                        }}
                        className="flex-1"
                      >
                        <XCircle size={18} className="mr-2" />
                        Reject
                      </Button>
                      <Button
                        variant="secondary"
                        onClick={() => {
                          setContactData({ notes: '', method: 'email' })
                          setShowContactModal(true)
                        }}
                      >
                        <MessageSquare size={18} className="mr-2" />
                        Mark Contacted
                      </Button>
                    </div>
                  ) : null}
                </div>
              </Card>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Approval Modal */}
      <AnimatePresence>
        {showApprovalModal && (
          <>
            <motion.div
              className="fixed inset-0 bg-black/30 backdrop-blur-sm z-40"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowApprovalModal(false)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="fixed inset-0 z-50 flex items-center justify-center p-4"
            >
              <Card variant="glass" className="w-full max-w-md p-6">
                <h3 className="text-xl font-bold mb-4">Approve Request</h3>
                <div className="space-y-4">
                  <Input
                    label="Organization Name"
                    value={approvalData.name}
                    onChange={(e) =>
                      setApprovalData({ ...approvalData, name: e.target.value })
                    }
                  />
                  <Select
                    label="Initial Tier"
                    value={approvalData.tier}
                    onChange={(e) =>
                      setApprovalData({ ...approvalData, tier: e.target.value })
                    }
                    options={[
                      { label: 'Demo', value: 'demo' },
                      { label: 'Essential', value: 'essential' },
                      { label: 'Professional', value: 'professional' },
                      { label: 'Enterprise', value: 'growth' }, // Code is 'growth', display name is 'Enterprise'
                      { label: 'Lite', value: 'lite' },
                    ]}
                  />
                  <div className="flex gap-3">
                    <Button onClick={handleApprove} className="flex-1">
                      Approve
                    </Button>
                    <Button
                      variant="secondary"
                      onClick={() => setShowApprovalModal(false)}
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              </Card>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Contact Modal */}
      <AnimatePresence>
        {showContactModal && (
          <>
            <motion.div
              className="fixed inset-0 bg-black/30 backdrop-blur-sm z-40"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowContactModal(false)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="fixed inset-0 z-50 flex items-center justify-center p-4"
            >
              <Card variant="glass" className="w-full max-w-md p-6">
                <h3 className="text-xl font-bold mb-4">Add Contact Notes</h3>
                <div className="space-y-4">
                  <Select
                    label="Contact Method"
                    value={contactData.method}
                    onChange={(e) =>
                      setContactData({ ...contactData, method: e.target.value })
                    }
                    options={[
                      { label: 'Email', value: 'email' },
                      { label: 'Phone', value: 'phone' },
                      { label: 'Both', value: 'both' },
                    ]}
                  />
                  <Textarea
                    label="Contact Notes"
                    value={contactData.notes}
                    onChange={(e) =>
                      setContactData({ ...contactData, notes: e.target.value })
                    }
                    rows={4}
                    placeholder="Notes about the contact..."
                  />
                  <div className="flex gap-3">
                    <Button onClick={handleMarkContacted} className="flex-1">
                      Save
                    </Button>
                    <Button
                      variant="secondary"
                      onClick={() => setShowContactModal(false)}
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              </Card>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  )
}

export default MasterAdminOrganizationRequests

